import React, { Fragment, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import T from 'prop-types';
import cx from 'classnames';
import { isEmpty } from 'lodash-es';

import ProcessTrack from './ProcessTrack';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import { portalNode, titlesByProcess, stepsOptions } from '../config';

const initialStepsState = {
  kpi: undefined,
  metric_name: undefined,
  correlation_r: undefined,
  industry_benchmark: undefined,
  report_generation: undefined,
};

const GenerationProcess = ({ company, onGetReport, onProcessed }) => {
  const [stepsState, setStepsState] = useState(initialStepsState);
  const [step, setStep] = useState();

  const correlation = (x) => {
    if (x >= 0.9) return `Near perfect  (r = ${x})`;
    if (x >= 0.75) return `Very strong  (r = ${x})`;
    if (x >= 0.5) return `Strong  (r = ${x})`;
    return `Weak  (r = ${x})`;
  };

  const stepNotes = {
    kpi: company.kpi,
    metric_name: company.metric_name,
    correlation_r: correlation(parseFloat(company.correlation_r)),
    industry_benchmark: company.competitor_name,
    report_generation: undefined,
  };

  const handleStepState = (stepName, stepState) => {
    setStepsState(prevState => ({ ...prevState, [stepName]: stepState }));
  };

  const handleGetReport = () => {
    if (step === 'done') onGetReport();
  };

  useEffect(() => {
    let timeoutId;

    if (!isEmpty(company)) {
      timeoutId = setTimeout(() => { setStep('initial'); }, 500);
    } else {
      setStepsState(initialStepsState);
      setStep(undefined);
    }

    return () => clearTimeout(timeoutId);
  }, [company]);

  useEffect(() => {
    let timeoutId;

    const handleStepProcessing = (nextStep) => {
      handleStepState(nextStep, 'processing');
      timeoutId = setTimeout(() => {
        handleStepState(nextStep, 'processed');
        setStep(nextStep);
      }, 3000);
    };

    switch (step) {
      case 'initial':
        handleStepProcessing('kpi');
        break;
      case 'kpi':
        handleStepProcessing('metric_name');
        break;
      case 'metric_name':
        handleStepProcessing('correlation_r');
        break;
      case 'correlation_r':
        handleStepProcessing('industry_benchmark');
        break;
      case 'industry_benchmark':
        handleStepProcessing('report_generation');
        break;
      case 'report_generation':
        timeoutId = setTimeout(() => {
          setStep('done');
          onProcessed();
        }, 1000);
        break;
      default: break;
    }

    return () => clearTimeout(timeoutId);
  }, [step]);

  return createPortal((
    <div className="finance-free-report-generation">
      <div className="finance-free-report-generation-process">
        <div className="generation-process-tracks-container">
          <ProcessTrack stepsState={stepsState} />
        </div>
        <div className="generation-process-notes-container">
          {stepsOptions.map(({ value, text }) => (
            <div key={value} className="process-notes-item">
              <div className="process-notes-item-header">
                {stepsState[value] ? titlesByProcess[stepsState[value]][value] : text }
              </div>
              {stepNotes[value] && (
                <div className={cx('process-notes-item-body', { 'show-process-note': stepsState[value] === 'processed' })}>
                  <p>{stepNotes[value]}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <button onClick={handleGetReport} className={cx('btn processing-report-button', `process-${step}`)}>
        <span className="processing-report-button-inner">
          {step === 'done'
            ? <Fragment><span className="mr-xxs">Get the Report</span><span className="hicon-angle-right-alt text-12" /></Fragment>
            : <Fragment><LoadingSpinner size="sm" className="mr-xxs" id="processing-report-button" /><span className="ml-xxs">Processing..</span></Fragment>
          }
        </span>
      </button>
    </div>
  ), portalNode);
};

GenerationProcess.propTypes = {
  onGetReport: T.func.isRequired,
  onProcessed: T.func.isRequired,
  company: T.shape({
    correlation_r: T.string,
    kpi: T.string,
    metric_name: T.string,
  }).isRequired,
};

export default GenerationProcess;
